import { useState } from 'react';
import {
  _diaValido,
  _diasEntre,
  hoyMadrid,
  type Periodo,
  type SeleccionPeriodo,
} from '@/lib/llamadas/fechas';

// Presets + rango personalizado. Los presets se quedan porque son acceso rápido
// a lo que se mira el 90% de las veces; el rango es lo que convierte el cuadro
// en herramienta de análisis ("el mes pasado", "la semana de la feria").

const OPCIONES: { valor: Periodo; etiqueta: string }[] = [
  { valor: 'hoy', etiqueta: 'Hoy' },
  { valor: '7d', etiqueta: 'Últimos 7 días' },
  { valor: '30d', etiqueta: 'Últimos 30 días' },
];

const PERSONALIZADO = 'personalizado';

// Tope de tres años. Era de uno, puesto para que la gráfica no dibujase un
// peine de barras de menos de un píxel, y la 2ª vuelta adversarial señaló que
// no lo conseguía: a 366 días las barras ya median 0,84 px. O sea que el tope
// cobraba una restricción a cambio de nada. Ahora la serie se agrupa por semana
// o por mes (`granularidadPara`), el peine no existe y el límite pasa a ser lo
// único que de verdad limita: cuánto se descarga. Tres años de llamadas es lo
// que cabe pedir de una vez; por encima, el guardarraíl de filas ya avisa.
export const MAX_DIAS = 1096;

// Extraída y exportada para poder probarla con la tabla de bordes: es la única
// barrera entre el usuario y `resolverPeriodo`, que LANZA con un rango inválido.
//
// Cuenta los días con `_diasEntre`, el mismo motor que usará `resolverPeriodo`,
// en vez de repetir la aritmética con `Date.parse` como hacía antes. Dos
// aritméticas distintas para el mismo número es una discrepancia esperando:
// `Date.parse('0050-03-01')` da el año 50 y `Date.UTC(50, …)` daba 1950, así que
// el selector validaba un rango y el cuadro consultaba otro.
//
// La guarda es POSITIVA (`>= 1`), no solo `<= MAX_DIAS`: un rango que produzca
// cero o negativo —invertido, o desbordado— no puede pasar por ser "pequeño".
export function _rangoValido(
  diaDesde: string,
  diaHasta: string
): { ok: boolean; demasiadoLargo: boolean } {
  if (!_diaValido(diaDesde) || !_diaValido(diaHasta)) return { ok: false, demasiadoLargo: false };
  const dias = _diasEntre(diaDesde, diaHasta);
  const demasiadoLargo = dias > MAX_DIAS;
  return { ok: dias >= 1 && !demasiadoLargo, demasiadoLargo };
}

export function SelectorPeriodo({
  seleccion,
  onCambiar,
}: {
  seleccion: SeleccionPeriodo;
  onCambiar: (s: SeleccionPeriodo) => void;
}) {
  const esRango = typeof seleccion !== 'string';
  const [abierto, setAbierto] = useState(esRango);
  const [diaDesde, setDiaDesde] = useState(esRango ? seleccion.diaDesde : hoyMadrid());
  const [diaHasta, setDiaHasta] = useState(esRango ? seleccion.diaHasta : hoyMadrid());

  const { ok: rangoValido, demasiadoLargo } = _rangoValido(diaDesde, diaHasta);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="Periodo"
        // También `abierto`, no solo `esRango`: entre elegir "Personalizado…" y
        // pulsar "Aplicar" no hay rango todavía, pero el desplegable debe seguir
        // mostrando lo que el usuario acaba de elegir. Si no, salta de vuelta al
        // preset anterior y parece que la elección no ha surtido efecto.
        value={esRango || abierto ? PERSONALIZADO : seleccion}
        onChange={(e) => {
          const v = e.target.value;
          if (v === PERSONALIZADO) {
            setAbierto(true);
            return; // no se emite nada hasta pulsar "Aplicar"
          }
          setAbierto(false);
          onCambiar(v as Periodo);
        }}
        className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12.5px] font-medium text-[var(--navy)] hover:bg-[var(--surface-2)] focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)] cursor-pointer"
      >
        {OPCIONES.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.etiqueta}
          </option>
        ))}
        <option value={PERSONALIZADO}>Personalizado…</option>
      </select>

      {abierto && (
        <div data-testid="rango-custom" className="flex flex-wrap items-center gap-1.5">
          <input
            type="date"
            aria-label="Desde"
            value={diaDesde}
            max={diaHasta}
            onChange={(e) => setDiaDesde(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-[12px] text-[var(--navy)] focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)]"
          />
          <span className="text-[11.5px]">a</span>
          <input
            type="date"
            aria-label="Hasta"
            value={diaHasta}
            min={diaDesde}
            onChange={(e) => setDiaHasta(e.target.value)}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2 py-1 text-[12px] text-[var(--navy)] focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)]"
          />
          <button
            type="button"
            disabled={!rangoValido}
            onClick={() => onCambiar({ diaDesde, diaHasta })}
            className="rounded-lg border border-[var(--border)] bg-[var(--surface)] px-2.5 py-1.5 text-[12px] font-medium text-[var(--navy)] hover:bg-[var(--surface-2)] disabled:cursor-not-allowed disabled:opacity-50 cursor-pointer focus-visible:outline-none focus-visible:border-[var(--orange)] focus-visible:shadow-[var(--ring)]"
          >
            Aplicar
          </button>
          {demasiadoLargo && (
            <span role="alert" className="text-[11.5px] text-[var(--orange-ink)]">
              Máximo tres años por consulta.
            </span>
          )}
        </div>
      )}
    </div>
  );
}
